import { useState, useEffect, useCallback, Fragment, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  Chip,
  Collapse,
  CircularProgress,
  Autocomplete,
  Checkbox,
  InputAdornment,
  Select,
  FormControl,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import type { Product, ProductDetail, User } from '../../types';
import { apiRequest } from '../../utils/api';
import { printBarcodes, printSingleBarcode } from '../../utils/printUtils';
import Barcode from 'react-barcode';

interface ProductProps {
  jobId?: number;
  asTab?: boolean;
}

// Natural sort function for room names (handles numeric sorting)
// This ensures "1", "2", "10" instead of "1", "10", "2"
const naturalSort = (a: string, b: string): number => {
  // Handle special cases
  if (a === '(No Room)') return 1;
  if (b === '(No Room)') return -1;
  
  // Extract numeric and non-numeric parts
  const regex = /(\d+)|(\D+)/g;
  const aParts = a.match(regex) || [];
  const bParts = b.match(regex) || [];
  
  const maxLength = Math.max(aParts.length, bParts.length);
  
  for (let i = 0; i < maxLength; i++) {
    const aPart = aParts[i] || '';
    const bPart = bParts[i] || '';
    
    const aIsNum = /^\d+$/.test(aPart);
    const bIsNum = /^\d+$/.test(bPart);
    
    if (aIsNum && bIsNum) {
      // Both are numbers, compare numerically
      const diff = parseInt(aPart, 10) - parseInt(bPart, 10);
      if (diff !== 0) return diff;
    } else if (aIsNum) {
      // a is number, b is not - numbers come first
      return -1;
    } else if (bIsNum) {
      // b is number, a is not - numbers come first
      return 1;
    } else {
      // Both are non-numeric, compare alphabetically
      const diff = aPart.localeCompare(bPart);
      if (diff !== 0) return diff;
    }
  }
  
  return 0;
};

export default function Product({ jobId, asTab = false }: ProductProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [products, setProducts] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [myWorkOnly] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [jobsWithUsers, setJobsWithUsers] = useState<Map<number, { job_number: string; user_id?: number; job_address?: string; apartment?: string }>>(new Map());
  const [roomsMap, setRoomsMap] = useState<Map<string, { status: string }>>(new Map());

  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState({
    product_name: '',
    room: '',
    status: 'Active',
    note: '',
    product_type_id: '',
    number_of_sections: '1',
    job_id: '',
  });

  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [detailForm, setDetailForm] = useState({
    width: '',
    height: '',
    number: '',
    status: '',
    barcode: '',
    typeOfWork: [] as string[],
  });
  const [selectedDetailId, setSelectedDetailId] = useState<number | null>(null);
  const [isEditDetail, setIsEditDetail] = useState(false);
  const [productTypes, setProductTypes] = useState<Array<{ id: number; productType: string; unit?: string | null }>>([]);
  const [productDetailStatuses, setProductDetailStatuses] = useState<Array<{ id: number; statusName: string }>>([]);
  const [typeOfWorkList, setTypeOfWorkList] = useState<Array<{ id: number; workName: string; isActive: boolean }>>([]);
  const [openCreateProductTypeDialog, setOpenCreateProductTypeDialog] = useState(false);
  const [createProductTypeForm, setCreateProductTypeForm] = useState({
    productType: '',
    description: '',
    sellingPrice: '',
    unit: 'Sq ft',
  });
  const [createProductTypeSaving, setCreateProductTypeSaving] = useState(false);
  const [productTypeInputValue, setProductTypeInputValue] = useState('');
  const [statusInputValue, setStatusInputValue] = useState<Map<number, string>>(new Map());
  const [typeOfWorkInputValue, setTypeOfWorkInputValue] = useState<Map<number, string>>(new Map());
  const [copiedTypeOfWork, setCopiedTypeOfWork] = useState<string[]>([]);
  const [createStatusSaving, setCreateStatusSaving] = useState(false);
  const [openAddStatusDialog, setOpenAddStatusDialog] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<{ productId: number; detailId: number; statusName: string } | null>(null);
  const [newStatusForm, setNewStatusForm] = useState({
    statusName: '',
    description: '',
    color: '',
  });
  const [openBulkStatusDialog, setOpenBulkStatusDialog] = useState(false);
  const [bulkStatusProductId, setBulkStatusProductId] = useState<number | null>(null);
  const [bulkStatusValue, setBulkStatusValue] = useState<string>('');
  const [bulkStatusSaving, setBulkStatusSaving] = useState(false);
  const [openCloneDialog, setOpenCloneDialog] = useState(false);
  const [cloneSourceRoom, setCloneSourceRoom] = useState<string>('');
  const [cloneJobId, setCloneJobId] = useState<number | null>(null);
  const [cloneRoomRange, setCloneRoomRange] = useState({ start: '', end: '' });
  const [cloneSaving, setCloneSaving] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const showError = (message: string) =>
    setSnackbar({ open: true, message, severity: 'error' });
  const showSuccess = (message: string) =>
    setSnackbar({ open: true, message, severity: 'success' });

  const loadCurrentUser = async () => {
    try {
      const response = await apiRequest('/users/profile/me', { method: 'GET' });
      setCurrentUser(response.data as User);
    } catch (error) {
      console.error('Failed to load current user:', error);
    }
  };

  // Check if user is a Field Worker
  const isFieldWorker = currentUser?.role === 'FIELD AND SHOP TEAM';
  const isAdmin = currentUser?.role === 'SYSTEM ADMIN';

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      if (jobId) {
        // Load products for specific job
        response = await apiRequest(`/products/job/${jobId}`, {
          method: 'GET',
        });
      } else {
        // Load all products
        response = await apiRequest('/products', {
          method: 'GET',
        });
      }
      // Products now come with details included from the backend
      const data = (response.data || []) as Product[];
      
      // Load job information for all unique job_ids first
      const uniqueJobIds = [...new Set(data.map((p: Product) => p.job_id))];
      const jobsMap = new Map();
      await Promise.all(
        uniqueJobIds.map(async (jid) => {
          try {
            const response = await apiRequest(`/jobs/${jid}`, { method: 'GET' });
            const job = response.data as { id: number; job_number: string; user_id?: number; job_address?: string; apartment?: string };
            jobsMap.set(jid, { 
              job_number: job.job_number, 
              user_id: job.user_id,
              job_address: job.job_address,
              apartment: job.apartment
            });
          } catch (error) {
            console.error(`Failed to load job ${jid}:`, error);
          }
        })
      );
      setJobsWithUsers((prev) => {
        const newMap = new Map(prev);
        jobsMap.forEach((value, key) => {
          if (!newMap.has(key)) {
            newMap.set(key, value);
          }
        });
        return newMap;
      });

      // Load rooms for all unique job_ids
      const roomsMapNew = new Map<string, { status: string }>();
      await Promise.all(
        uniqueJobIds.map(async (jid) => {
          try {
            const roomsResponse = await apiRequest(`/products/rooms/job/${jid}`, { method: 'GET' });
            const roomsData = (roomsResponse.data || []) as Array<{ room_name: string; status: string }>;
            roomsData.forEach((room) => {
              const key = `${jid}-${room.room_name}`;
              roomsMapNew.set(key, { status: room.status });
            });
          } catch (error) {
            console.error(`Failed to load rooms for job ${jid}:`, error);
          }
        })
      );
      setRoomsMap(roomsMapNew);
      
      // Products already have details included from backend
      setAllProducts(data);
    } catch (error: any) {
      showError(error.message || 'Failed to load fabrication orders');
    } finally {
      setLoading(false);
    }
  }, [jobId, showError]);

  useEffect(() => {
    loadCurrentUser();
    loadProducts();
    loadProductTypes();
    loadProductDetailStatuses();
    loadTypeOfWork();
  }, [jobId]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce delay

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Filter products based on search and myWork (show all statuses) - optimized with useMemo
  const filteredProducts = useMemo(() => {
    return allProducts.filter((product) => {
      // Filter by search term
      if (debouncedSearchTerm) {
        const searchLower = debouncedSearchTerm.toLowerCase();
        const matchesSearch =
          product.product_name?.toLowerCase().includes(searchLower) ||
          product.room?.toLowerCase().includes(searchLower) ||
          product.note?.toLowerCase().includes(searchLower) ||
          jobsWithUsers.get(product.job_id)?.job_number?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) {
          return false;
        }
      }

      // Filter by "my work" - check if job's user_id matches current user
      if (myWorkOnly && currentUser) {
        const job = jobsWithUsers.get(product.job_id);
        if (job?.user_id !== currentUser.id) {
          return false;
        }
      }

      return true;
    });
  }, [allProducts, debouncedSearchTerm, myWorkOnly, currentUser, jobsWithUsers]);

  // Update products when filtered products change
  useEffect(() => {
    setProducts(filteredProducts);
    setPage(0); // Reset to first page when filters change
    // Clear selection when products change (filtering, etc.)
    setSelectedProducts(new Set());
  }, [filteredProducts]);

  // Paginate products (not rooms) - calculate which products to show
  const paginatedProducts = useMemo(() => {
    // Flatten all products with their room info
    const productsWithRooms = products.flatMap((product) => ({
      product,
      room: product.room || '(No Room)',
    }));

    // Calculate start and end indices
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    // Get paginated products
    const paginated = productsWithRooms.slice(startIndex, endIndex);

    // Group paginated products back by room
    const paginatedByRoom: Record<string, Product[]> = {};
    paginated.forEach(({ product, room }) => {
      if (!paginatedByRoom[room]) {
        paginatedByRoom[room] = [];
      }
      paginatedByRoom[room].push(product);
    });

    return {
      products: paginated,
      byRoom: paginatedByRoom,
      totalProducts: products.length,
    };
  }, [products, page, rowsPerPage]);

  // Get unique room list from all products (excluding empty/null and "(No Room)")
  const availableRooms = useMemo(() => {
    const rooms = new Set<string>();
    allProducts.forEach((product) => {
      if (product.room && product.room.trim() !== '' && product.room !== '(No Room)') {
        rooms.add(product.room.trim());
      }
    });
    return Array.from(rooms).sort(naturalSort);
  }, [allProducts]);

  const loadProductTypes = async () => {
    try {
      const response = await apiRequest('/producttype', { method: 'GET' });
      const data = (response.data || []) as Array<{ id: number; productType: string; unit?: string | null }>;
      setProductTypes(data);
    } catch (error) {
      console.error('Failed to load product types:', error);
    }
  };

  const loadProductDetailStatuses = async () => {
    try {
      const response = await apiRequest('/product-detail-status', { method: 'GET' });
      const data = (response.data || []) as Array<{ id: number; statusName: string; isActive: boolean }>;
      // Filter only active statuses and sort by status name
      const activeStatuses = data
        .filter((status) => status.isActive)
        .sort((a, b) => a.statusName.localeCompare(b.statusName));
      setProductDetailStatuses(activeStatuses);
    } catch (error) {
      console.error('Failed to load product detail statuses:', error);
      // Fallback to empty array if API fails
      setProductDetailStatuses([]);
    }
  };

  const loadTypeOfWork = async () => {
    try {
      const response = await apiRequest('/type-of-work', { method: 'GET' });
      const data = (response.data || []) as Array<{ id: number; workName: string; isActive: boolean }>;
      // Filter only active types and sort by work name
      const activeTypes = data
        .filter((type) => type.isActive)
        .sort((a, b) => a.workName.localeCompare(b.workName));
      setTypeOfWorkList(activeTypes);
    } catch (error) {
      console.error('Failed to load type of work:', error);
      setTypeOfWorkList([]);
    }
  };

  const handleCreateProductType = async () => {
    if (!createProductTypeForm.productType || !createProductTypeForm.sellingPrice) {
      showError('Product Type and Selling Price are required');
      return;
    }

    const numericSellingPrice = Number(createProductTypeForm.sellingPrice);

    if (Number.isNaN(numericSellingPrice)) {
      showError('Selling Price must be a valid number');
      return;
    }

    setCreateProductTypeSaving(true);
    try {
      const response = await apiRequest('/producttype', {
        method: 'PUT',
        body: JSON.stringify({
          productType: createProductTypeForm.productType,
          description: createProductTypeForm.description || null,
          sellingPrice: numericSellingPrice,
          unit: createProductTypeForm.unit || null,
        }),
      });
      const data = response.data as { productType: { id: number; productType: string; unit?: string | null } };
      
      // Add the new product type to the list
      setProductTypes((prev) => [
        {
          id: data.productType.id,
          productType: data.productType.productType,
          unit: data.productType.unit,
        },
        ...prev,
      ]);

      // Auto-select the newly created product type
      setForm({
        ...form,
        product_type_id: data.productType.id.toString(),
      });

      showSuccess('Product type created successfully');
      setOpenCreateProductTypeDialog(false);
      setCreateProductTypeForm({
        productType: '',
        description: '',
        sellingPrice: '',
        unit: 'Sq ft',
      });
      setProductTypeInputValue('');
    } catch (error: any) {
      showError(error.message || 'Failed to create product type');
    } finally {
      setCreateProductTypeSaving(false);
    }
  };

  const generateDetailName = useCallback((
    product: Product,
    detail: ProductDetail,
    totalCount: number
  ): string => {
    if (!jobsWithUsers || !productTypes) {
      return `${product.job_id}-${detail.number || ''}-${totalCount}`;
    }
    
    const job = jobsWithUsers.get(product.job_id);
    const jobNumber = job?.job_number || '';
    const room = product.room || '';
    const order = product.order || '';
    const detailNumber = detail.number || '';
    
    // Check if product detail's product type unit is "Hardware"
    const productType = productTypes.find((pt) => pt.id === detail.product_type_id);
    const isHardware = productType?.unit === 'Hardware';
    
    const parts = [
      product.job_id.toString(),
      jobNumber,
      room,
      order.toString(),
    ];
    
    if (isHardware) {
      parts.push('HW');
    }
    
    parts.push(detailNumber.toString());
    
    return `${parts.join('-')}-${totalCount}`;
  }, [jobsWithUsers, productTypes]);

  const handleToggleRow = useCallback((productId: number) => {
    setExpandedRows((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(productId)) {
        newExpanded.delete(productId);
      } else {
        newExpanded.add(productId);
      }
      return newExpanded;
    });
  }, []);

  const handleOpenDialog = (product?: Product) => {
    if (product) {
      setIsEdit(true);
      setSelectedId(product.id);
      const selectedProductType = productTypes.find((pt) => pt.id === product.product_type_id);
      setForm({
        product_name: product.product_name || '',
        room: product.room || '',
        status: product.status || '',
        note: product.note || '',
        product_type_id: product.product_type_id?.toString() || '',
        number_of_sections: '1',
        job_id: product.job_id.toString(),
      });
      setProductTypeInputValue(selectedProductType?.productType || '');
    } else {
      setIsEdit(false);
      setSelectedId(null);
      setForm({
        product_name: '',
        room: '',
        status: 'Active',
        note: '',
        product_type_id: '',
        number_of_sections: '1',
        job_id: jobId ? jobId.toString() : '',
      });
      setProductTypeInputValue('');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsEdit(false);
    setSelectedId(null);
    setForm({
      product_name: '',
      room: '',
      status: '',
      note: '',
      product_type_id: '',
      number_of_sections: '1',
      job_id: jobId ? jobId.toString() : '',
    });
    setProductTypeInputValue('');
  };

  const handleSave = async () => {
    if (!form.product_name.trim()) {
      showError('Product name is required');
      return;
    }
    if (!form.room || !form.room.trim()) {
      showError('Room is required');
      return;
    }
    if (!form.job_id) {
      showError('Job ID is required');
      return;
    }

    // Check if user typed a new product type but didn't create it yet
    if (productTypeInputValue && !form.product_type_id) {
      const exists = productTypes.some(
        (pt) => pt.productType.toLowerCase() === productTypeInputValue.toLowerCase()
      );
      if (!exists) {
        // Open dialog to create the new product type
        setCreateProductTypeForm({
          ...createProductTypeForm,
          productType: productTypeInputValue,
        });
        setOpenCreateProductTypeDialog(true);
        showError('Please create the product type first or select an existing one');
        return;
      }
    }

    setSaving(true);
    try {
      if (isEdit && selectedId) {
        await apiRequest(`/products/${selectedId}`, {
          method: 'PUT',
          body: JSON.stringify({
            product_name: form.product_name,
            room: form.room || null,
            status: form.status || null,
            note: form.note || null,
            product_type_id: form.product_type_id ? parseInt(form.product_type_id) : null,
            job_id: parseInt(form.job_id),
          }),
        });
        showSuccess('Fabrication order updated successfully');
      } else {
        await apiRequest('/products', {
          method: 'POST',
          body: JSON.stringify({
            product_name: form.product_name,
            room: form.room || null,
            status: form.status || null,
            note: form.note || null,
            product_type_id: form.product_type_id ? parseInt(form.product_type_id) : null,
            number_of_sections: parseInt(form.number_of_sections) || 1,
            job_id: parseInt(form.job_id),
          }),
        });
        showSuccess('Fabrication order created successfully');
      }
      handleCloseDialog();
      loadProducts();
    } catch (error: any) {
      showError(error.message || 'Failed to save fabrication order');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = useCallback(async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this fabrication order?')) {
      return;
    }

    try {
      await apiRequest(`/products/${id}`, {
        method: 'DELETE',
      });
      showSuccess('Fabrication order deleted successfully');
      loadProducts();
      // Remove from selection if it was selected
      setSelectedProducts((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    } catch (error: any) {
      showError(error.message || 'Failed to delete fabrication order');
    }
  }, [loadProducts, showSuccess, showError]);

  // Handle selecting/deselecting a product
  const handleSelectProduct = useCallback((productId: number) => {
    setSelectedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  }, []);

  // Handle selecting/deselecting all products
  const handleSelectAll = useCallback(() => {
    if (selectedProducts.size === products.length && products.length > 0) {
      // Deselect all
      setSelectedProducts(new Set());
    } else {
      // Select all
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  }, [selectedProducts.size, products]);

  // Handle bulk delete
  const handleBulkDelete = useCallback(async () => {
    if (selectedProducts.size === 0) {
      showError('No products selected');
      return;
    }

    const count = selectedProducts.size;
    if (!window.confirm(`Are you sure you want to delete ${count} fabrication order(s)?`)) {
      return;
    }

    setBulkDeleting(true);
    try {
      // Delete all selected products in parallel
      const deletePromises = Array.from(selectedProducts).map(id =>
        apiRequest(`/products/${id}`, {
          method: 'DELETE',
        })
      );

      await Promise.all(deletePromises);
      showSuccess(`Successfully deleted ${count} fabrication order(s)`);
      setSelectedProducts(new Set());
      loadProducts();
    } catch (error: any) {
      showError(error.message || 'Failed to delete fabrication orders');
    } finally {
      setBulkDeleting(false);
    }
  }, [selectedProducts, loadProducts, showSuccess, showError]);

  const buildPrintName = (product: Product, detail: ProductDetail) => {
    const totalCount = product.details?.length || 0;
    const productType = productTypes.find((pt) => pt.id === detail.product_type_id);
    const isHardware = productType?.unit === 'Hardware';

    const parts: string[] = [product.job_id.toString()];
    if (product.room) parts.push(product.room);
    if (isHardware) parts.push('HW');
    if (detail.number) parts.push(detail.number.toString());
    return `${parts.join('-')}${totalCount > 0 ? `-${totalCount}` : ''}`;
  };

  const handlePrintBarcodes = (product: Product) => {
    if (!product.details || product.details.length === 0) {
      showError('No fabrication order details to print');
      return;
    }

    const job = jobsWithUsers.get(product.job_id);
    const jobAddress = job?.job_address || '';

    const items = product.details
      .filter((detail) => detail.barcode)
      .map((detail) => {
        const productType = productTypes.find((pt) => pt.id === detail.product_type_id);
        return {
          barcode: detail.barcode!,
          name: buildPrintName(product, detail),
          productName: product.product_name,
          room: product.room || '',
          address: jobAddress,
          productTypeName: productType ? productType.productType : '',
          width: detail.width,
          height: detail.height,
        };
      });

    if (items.length === 0) {
      showError('No barcodes found to print');
      return;
    }

    printBarcodes(items, {
      title: `Print Barcodes - ${product.product_name}`,
      barcodeWidth: 4,
      barcodeHeight: 200,
      displayValue: false,
    });
  };

  const handlePrintSingleBarcode = (product: Product, detail: ProductDetail) => {
    if (!detail.barcode) {
      showError('No barcode to print');
      return;
    }

    const job = jobsWithUsers.get(product.job_id);
    const jobAddress = job?.job_address || '';
    const productType = productTypes.find((pt) => pt.id === detail.product_type_id);

    printSingleBarcode({
      barcode: detail.barcode,
      name: buildPrintName(product, detail),
      productName: product.product_name,
      room: product.room || '',
      address: jobAddress,
      productTypeName: productType ? productType.productType : '',
      width: detail.width,
      height: detail.height,
    });
  };

  const handleOpenDetailDialog = (productId: number, detail?: ProductDetail) => {
    setSelectedProductId(productId);
    if (detail) {
      setIsEditDetail(true);
      setSelectedDetailId(detail.id);
      setDetailForm({
        width: detail.width?.toString() || '',
        height: detail.height?.toString() || '',
        number: detail.number?.toString() || '',
        status: detail.status || '',
        barcode: detail.barcode || '',
        typeOfWork: detail.type_of_work ? (typeof detail.type_of_work === 'string' ? detail.type_of_work.split(',').map(s => s.trim()).filter(s => s) : (Array.isArray(detail.type_of_work) ? detail.type_of_work : [])) : [],
      });
    } else {
      setIsEditDetail(false);
      setSelectedDetailId(null);
      setDetailForm({
        width: '',
        height: '',
        number: '',
        status: '',
        barcode: '',
        typeOfWork: [],
      });
    }
    setOpenDetailDialog(true);
  };

  // Check if product type unit is Hardware for the detail dialog
  const isHardwareProduct = (): boolean => {
    if (!selectedProductId) return false;
    
    // If editing, check the detail's product_type_id
    if (isEditDetail && selectedDetailId) {
      const product = products.find((p) => p.id === selectedProductId);
      if (!product || !product.details) return false;
      const detail = product.details.find((d) => d.id === selectedDetailId);
      if (!detail || !detail.product_type_id) return false;
      const productType = productTypes.find((pt) => pt.id === detail.product_type_id);
      return productType?.unit === 'Hardware';
    }
    
    // If adding new, check the product's product_type_id
    const product = products.find((p) => p.id === selectedProductId);
    if (!product || !product.product_type_id) return false;
    const productType = productTypes.find((pt) => pt.id === product.product_type_id);
    return productType?.unit === 'Hardware';
  };

  // Check if detail's product type unit is Hardware
  const isHardwareDetail = (detail: ProductDetail): boolean => {
    if (!detail.product_type_id) return false;
    const productType = productTypes.find((pt) => pt.id === detail.product_type_id);
    return productType?.unit === 'Hardware';
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setSelectedProductId(null);
    setIsEditDetail(false);
    setSelectedDetailId(null);
    setDetailForm({
      width: '',
      height: '',
      number: '',
      status: '',
      barcode: '',
      typeOfWork: [],
    });
  };

  const handleSaveDetail = async () => {
    if (!selectedProductId) return;

    const isHardware = isHardwareProduct();
    setSaving(true);
    try {
      if (isEditDetail && selectedDetailId) {
        await apiRequest(`/products/${selectedProductId}/details/${selectedDetailId}`, {
          method: 'PUT',
          body: JSON.stringify({
            width: isHardware ? null : (detailForm.width ? parseFloat(detailForm.width) : null),
            height: isHardware ? null : (detailForm.height ? parseFloat(detailForm.height) : null),
            number: detailForm.number ? parseInt(detailForm.number) : null,
            status: detailForm.status || null,
            barcode: detailForm.barcode || null,
            type_of_work: detailForm.typeOfWork.length > 0 ? detailForm.typeOfWork.join(', ') : null,
          }),
        });
        showSuccess('Fabrication order detail updated successfully');
      } else {
        // Get product_type_id from the product
        const product = products.find((p) => p.id === selectedProductId);
        const productTypeId = product?.product_type_id || null;
        
        await apiRequest(`/products/${selectedProductId}/details`, {
          method: 'POST',
          body: JSON.stringify({
            product_id: selectedProductId,
            product_type_id: productTypeId,
            width: isHardware ? null : (detailForm.width ? parseFloat(detailForm.width) : null),
            height: isHardware ? null : (detailForm.height ? parseFloat(detailForm.height) : null),
            number: detailForm.number ? parseInt(detailForm.number) : null,
            status: detailForm.status || null,
            barcode: detailForm.barcode || null,
            type_of_work: detailForm.typeOfWork.length > 0 ? detailForm.typeOfWork.join(', ') : null,
          }),
        });
        showSuccess('Fabrication order detail created successfully');
      }
      handleCloseDetailDialog();
      loadProducts();
    } catch (error: any) {
      showError(error.message || 'Failed to save fabrication order detail');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDetail = async (productId: number, detailId: number) => {
    if (!window.confirm('Are you sure you want to delete this fabrication order detail?')) {
      return;
    }

    try {
      await apiRequest(`/products/${productId}/details/${detailId}`, {
        method: 'DELETE',
      });
      showSuccess('Fabrication order detail deleted successfully');
      loadProducts();
    } catch (error: any) {
      showError(error.message || 'Failed to delete fabrication order detail');
    }
  };

  const handleDetailStatusChange = async (productId: number, detailId: number, newStatus: string | null) => {
    // Check if user is a Field Worker
    if (isFieldWorker) {
      showError('FIELD AND SHOP TEAM cannot change status');
      return;
    }

    try {
      // If status is empty or null, save as null (which represents 'None' in database)
      const statusToSave = (!newStatus || newStatus.trim() === '') ? null : newStatus;

      await apiRequest(`/products/${productId}/details/${detailId}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: statusToSave,
        }),
      });
      
      // Update local state - store null as null (will display as empty in UI)
      setAllProducts((prev) => {
        return prev.map((product) => {
          if (product.id === productId && product.details) {
            return {
              ...product,
              details: product.details.map((detail) =>
                detail.id === detailId ? { ...detail, status: statusToSave } : detail
              ),
            };
          }
          return product;
        });
      });
      
      showSuccess('Status updated successfully');
    } catch (error: any) {
      showError(error.message || 'Failed to update status');
    }
  };

  // Handle opening add status modal
  const handleOpenAddStatusModal = (productId: number, detailId: number, statusName: string) => {
    setPendingStatus({ productId, detailId, statusName });
    setNewStatusForm({
      statusName: statusName.trim(),
      description: '',
      color: '',
    });
    setOpenAddStatusDialog(true);
  };

  // Handle saving new status from modal
  const handleSaveNewStatus = async () => {
    if (!newStatusForm.statusName || !newStatusForm.statusName.trim()) {
      showError('Status name is required');
      return;
    }

    if (!pendingStatus) {
      return;
    }

    setCreateStatusSaving(true);
    try {
      const response = await apiRequest('/product-detail-status', {
        method: 'PUT',
        body: JSON.stringify({
          statusName: newStatusForm.statusName.trim(),
          description: newStatusForm.description || null,
          color: newStatusForm.color || null,
          isActive: true,
        }),
      });
      const newStatus = (response.data as { productDetailStatus: { id: number; statusName: string; isActive: boolean } })
        .productDetailStatus;
      
      // Add to the list and sort
      setProductDetailStatuses((prev) => {
        const updated = [...prev, newStatus];
        return updated.sort((a, b) => a.statusName.localeCompare(b.statusName));
      });
      
      // Update the status for the pending detail
      if (pendingStatus.productId === -1 && pendingStatus.detailId === -1) {
        // This is from the dialog form, just update the form state
        setDetailForm({ ...detailForm, status: newStatus.statusName });
        const newMap = new Map(statusInputValue);
        newMap.set(-1, newStatus.statusName);
        setStatusInputValue(newMap);
      } else {
        // This is from the table view, update the detail status
        await handleDetailStatusChange(pendingStatus.productId, pendingStatus.detailId, newStatus.statusName);
      }
      
      showSuccess(`Status "${newStatusForm.statusName}" created successfully`);
      setOpenAddStatusDialog(false);
      setPendingStatus(null);
      setNewStatusForm({ statusName: '', description: '', color: '' });
    } catch (error) {
      console.error('Error creating status:', error);
      showError(
        error instanceof Error ? error.message : 'Failed to create status'
      );
    } finally {
      setCreateStatusSaving(false);
    }
  };

  // Status options for product details - loaded from API
  const detailStatusOptions = useMemo(() => {
    // Start with "None" as the first option
    const options = ['None'];
    // Add all active status names from the API (excluding "None" if it exists)
    productDetailStatuses.forEach((status) => {
      if (status.statusName !== 'None') {
        options.push(status.statusName);
      }
    });
    return options;
  }, [productDetailStatuses]);

  // Type of work options for product details - loaded from API
  const typeOfWorkOptions = useMemo(() => {
    return typeOfWorkList.map((type) => type.workName);
  }, [typeOfWorkList]);

  // Handle creating new type of work
  const handleCreateTypeOfWork = async (workName: string, addToForm: boolean = false): Promise<string | null> => {
    if (!workName || !workName.trim()) {
      showError('Type of work name is required');
      return null;
    }

    try {
      const response = await apiRequest('/type-of-work', {
        method: 'PUT',
        body: JSON.stringify({
          workName: workName.trim(),
          description: null,
          isActive: true,
        }),
      });
      const newTypeOfWork = (response.data as { typeOfWork: { id: number; workName: string; isActive: boolean } })
        .typeOfWork;
      
      // Add to the list and sort
      setTypeOfWorkList((prev) => {
        const updated = [...prev, newTypeOfWork];
        return updated.sort((a, b) => a.workName.localeCompare(b.workName));
      });
      
      // Update the form state if requested
      if (addToForm) {
        const currentValues = Array.isArray(detailForm.typeOfWork) ? detailForm.typeOfWork : [];
        if (!currentValues.includes(newTypeOfWork.workName)) {
          setDetailForm({ ...detailForm, typeOfWork: [...currentValues, newTypeOfWork.workName] });
        }
      }
      
      const newMap = new Map(typeOfWorkInputValue);
      newMap.set(-1, '');
      setTypeOfWorkInputValue(newMap);
      
      showSuccess(`Type of work "${newTypeOfWork.workName}" created successfully`);
      return newTypeOfWork.workName;
    } catch (error) {
      console.error('Error creating type of work:', error);
      showError(
        error instanceof Error ? error.message : 'Failed to create type of work'
      );
      return null;
    }
  };

  // Handle opening bulk status change dialog
  const handleOpenBulkStatusDialog = (productId: number) => {
    // Check if user is a Field Worker
    if (isFieldWorker) {
      showError('FIELD AND SHOP TEAM cannot change status');
      return;
    }
    setBulkStatusProductId(productId);
    setBulkStatusValue('');
    setOpenBulkStatusDialog(true);
  };

  // Handle closing bulk status change dialog
  const handleCloseBulkStatusDialog = () => {
    setOpenBulkStatusDialog(false);
    setBulkStatusProductId(null);
    setBulkStatusValue('');
  };

  // Handle opening clone room dialog
  const handleOpenCloneDialog = (room: string, jobId: number) => {
    setCloneSourceRoom(room);
    setCloneJobId(jobId);
    setCloneRoomRange({ start: '', end: '' });
    setOpenCloneDialog(true);
  };

  // Handle closing clone room dialog
  const handleCloseCloneDialog = () => {
    setOpenCloneDialog(false);
    setCloneSourceRoom('');
    setCloneJobId(null);
    setCloneRoomRange({ start: '', end: '' });
  };

  // Handle cloning room
  const handleCloneRoom = async () => {
    if (!cloneJobId || !cloneSourceRoom) {
      showError('Invalid source room or job ID');
      return;
    }

    if (!cloneRoomRange.start || !cloneRoomRange.end) {
      showError('Please enter both start and end room numbers');
      return;
    }

    const start = parseInt(cloneRoomRange.start);
    const end = parseInt(cloneRoomRange.end);

    if (isNaN(start) || isNaN(end)) {
      showError('Room numbers must be valid integers');
      return;
    }

    if (start > end) {
      showError('Start room number must be less than or equal to end room number');
      return;
    }

    if (end - start > 200) {
      showError('Cannot clone more than 200 rooms at once. Please use a smaller range.');
      return;
    }

    // Extract prefix from source room name (e.g., "Bathroom" from "Bathroom 1")
    // Try to find the last number in the room name and extract everything before it
    const sourceRoomTrimmed = cloneSourceRoom.trim();
    const numberMatch = sourceRoomTrimmed.match(/(\d+)$/);
    let roomPrefix = sourceRoomTrimmed;
    
    if (numberMatch) {
      // Extract everything before the last number (with trailing space removed)
      const numberIndex = sourceRoomTrimmed.lastIndexOf(numberMatch[1]);
      roomPrefix = sourceRoomTrimmed.substring(0, numberIndex).trim();
    }

    // Generate array of room names with prefix
    const targetRooms: string[] = [];
    for (let i = start; i <= end; i++) {
      // Combine prefix with room number (e.g., "Bathroom 2", "Bathroom 3")
      const roomName = roomPrefix ? `${roomPrefix} ${i}` : i.toString();
      targetRooms.push(roomName);
    }

    setCloneSaving(true);
    try {
      const response = await apiRequest('/products/clone-room', {
        method: 'POST',
        body: JSON.stringify({
          job_id: cloneJobId,
          source_room: cloneSourceRoom,
          target_rooms: targetRooms,
        }),
      });

      const data = response.data as { message: string; clonedRooms: string[]; errors?: string[] };
      
      if (data.errors && data.errors.length > 0) {
        showError(`Cloned ${data.clonedRooms.length} rooms, but encountered ${data.errors.length} error(s). Check console for details.`);
        console.error('Cloning errors:', data.errors);
      } else {
        showSuccess(`Successfully cloned room "${cloneSourceRoom}" to ${data.clonedRooms.length} room(s)`);
      }
      
      handleCloseCloneDialog();
      loadProducts();
    } catch (error: any) {
      showError(error.message || 'Failed to clone room');
    } finally {
      setCloneSaving(false);
    }
  };

  // Handle bulk status change
  const handleBulkStatusChange = async () => {
    if (!bulkStatusProductId) return;

    // Check if user is a Field Worker
    if (isFieldWorker) {
      showError('FIELD AND SHOP TEAM cannot change status');
      handleCloseBulkStatusDialog();
      return;
    }

    const product = allProducts.find((p) => p.id === bulkStatusProductId);
    if (!product || !product.details || product.details.length === 0) {
      showError('No product details found');
      return;
    }

    // Check if status exists (unless it's "None" or empty)
    let statusToSave: string | null = null;
    if (bulkStatusValue && bulkStatusValue.trim() !== '' && bulkStatusValue !== 'None') {
      const trimmedStatus = bulkStatusValue.trim();
      
      // Check if it's a create option
      if (trimmedStatus.startsWith('__CREATE__:')) {
        showError('Please create the status first before applying it to all details');
        return;
      }
      
      // Check if status exists in options
      if (!detailStatusOptions.includes(trimmedStatus)) {
        showError(`Status "${trimmedStatus}" does not exist. Please create it first or select an existing status.`);
        return;
      }
      
      statusToSave = trimmedStatus;
    }

    setBulkStatusSaving(true);
    try {
      // Update all details in parallel
      const updatePromises = product.details.map((detail) =>
        apiRequest(`/products/${bulkStatusProductId}/details/${detail.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            status: statusToSave,
          }),
        })
      );

      await Promise.all(updatePromises);

      // Update local state
      setAllProducts((prev) => {
        return prev.map((p) => {
          if (p.id === bulkStatusProductId && p.details) {
            return {
              ...p,
              details: p.details.map((detail) => ({
                ...detail,
                status: statusToSave,
              })),
            };
          }
          return p;
        });
      });

      // Clear status input values for all updated details
      setStatusInputValue((prev) => {
        const newMap = new Map(prev);
        if (product.details) {
          product.details.forEach((detail) => {
            newMap.set(detail.id, statusToSave || '');
          });
        }
        return newMap;
      });

      showSuccess(`Status updated for all ${product.details.length} product details`);
      handleCloseBulkStatusDialog();
    } catch (error: any) {
      showError(error.message || 'Failed to update statuses');
    } finally {
      setBulkStatusSaving(false);
    }
  };

  return (
    <Box>
      {!asTab && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary">
            Fabrication Orders
          </Typography>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Fabrication Order Management
          </Typography>
        </Box>
      )}

      <Paper>
        <Toolbar sx={{ 
          justifyContent: 'space-between', 
          flexWrap: 'wrap', 
          gap: 2,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          px: isMobile ? 1 : 2,
          py: isMobile ? 2 : 1,
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            flexWrap: 'wrap',
            width: isMobile ? '100%' : 'auto',
          }}>
            <TextField
              placeholder="Search..."
              size={isMobile ? 'medium' : 'small'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                minWidth: isMobile ? '100%' : 200,
                flex: isMobile ? 1 : 'none',
              }}
            />
          </Box>
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            alignItems: 'center',
            flexWrap: 'wrap',
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'space-between' : 'flex-end',
          }}>
            {selectedProducts.size > 0 && isAdmin && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                size={isMobile ? 'medium' : 'small'}
                sx={{ 
                  bgcolor: 'error.main', 
                  '&:hover': { bgcolor: 'error.dark' },
                  flex: isMobile ? 1 : 'none',
                  minWidth: isMobile ? 0 : 'auto',
                }}
              >
                {isMobile 
                  ? (bulkDeleting ? `Deleting...` : `Delete (${selectedProducts.size})`)
                  : (bulkDeleting ? `Deleting... (${selectedProducts.size})` : `Delete Selected (${selectedProducts.size})`)
                }
              </Button>
            )}
            <IconButton 
              onClick={loadProducts} 
              disabled={loading}
              size={isMobile ? 'medium' : 'small'}
              sx={{ 
                minWidth: isMobile ? 48 : 40,
                minHeight: isMobile ? 48 : 40,
              }}
            >
              <RefreshIcon fontSize={isMobile ? 'medium' : 'small'} />
            </IconButton>
            {!isFieldWorker && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                size={isMobile ? 'medium' : 'small'}
                sx={{ 
                  bgcolor: 'grey.900', 
                  '&:hover': { bgcolor: 'grey.800' },
                  flex: isMobile ? 1 : 'none',
                  minWidth: isMobile ? 0 : 'auto',
                }}
              >
                Create
              </Button>
            )}
          </Box>
        </Toolbar>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Counts Display */}
            <Box sx={{ 
              p: isMobile ? 1.5 : 2, 
              display: 'flex', 
              gap: isMobile ? 2 : 3, 
              borderBottom: 1, 
              borderColor: 'divider',
              flexDirection: isMobile ? 'column' : 'row',
            }}>
              <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ fontWeight: 600 }}>
                Room Count: {new Set(products.map(p => p.room || '(No Room)')).size}
              </Typography>
              <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ fontWeight: 600 }}>
                Product Count: {products.length}
              </Typography>
            </Box>
            {isMobile ? (
              // Mobile Card Layout
              <Box sx={{ p: 1 }}>
                {products.length === 0 ? (
                  <Box sx={{ textAlign: 'center', p: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      No fabrication orders found
                    </Typography>
                  </Box>
                ) : (() => {
                  const paginatedRooms = Object.keys(paginatedProducts.byRoom).sort(naturalSort);

                  return paginatedRooms.map((room) => {
                    const roomProducts = paginatedProducts.byRoom[room];
                    const firstProduct = roomProducts[0];
                    const trimmedRoom = room.trim();
                    const roomKey = firstProduct ? `${firstProduct.job_id}-${trimmedRoom}` : '';
                    const roomStatus = roomsMap.get(roomKey)?.status || 'active';

                    return (
                      <Card key={`room-${room}`} sx={{ mb: 2, overflow: 'visible' }}>
                        {/* Room Header */}
                        <CardContent sx={{ 
                          backgroundColor: 'primary.main', 
                          color: 'primary.contrastText',
                          p: 2,
                          '&:last-child': { pb: 2 },
                        }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                Room Name: "{room}"
                              </Typography>
                              {!isFieldWorker && firstProduct && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenCloneDialog(trimmedRoom, firstProduct.job_id)}
                                  sx={{
                                    color: 'primary.contrastText',
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                    },
                                    minWidth: 40,
                                    minHeight: 40,
                                  }}
                                  title="Clone this room"
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                            <FormControl size="small" fullWidth>
                              <Select
                                value={roomStatus}
                                onChange={(e) => {
                                  if (!firstProduct) return;
                                  const newStatus = e.target.value;
                                  
                                  apiRequest(`/products/rooms/job/${firstProduct.job_id}`, { method: 'GET' })
                                    .then((roomsResponse) => {
                                      const roomsData = (roomsResponse.data || []) as Array<{ id: number; room_name: string; status: string }>;
                                      const roomData = roomsData.find(r => r.room_name === trimmedRoom);
                                      
                                      if (roomData) {
                                        return apiRequest(`/products/rooms/${roomData.id}`, {
                                          method: 'PUT',
                                          body: JSON.stringify({ status: newStatus }),
                                        });
                                      }
                                      throw new Error('Room not found');
                                    })
                                    .then(() => {
                                      setRoomsMap((prev) => {
                                        const newMap = new Map(prev);
                                        newMap.set(roomKey, { status: newStatus });
                                        return newMap;
                                      });
                                      showSuccess('Room status updated successfully');
                                    })
                                    .catch((error: any) => {
                                      showError(error.message || 'Failed to update room status');
                                    });
                                }}
                                sx={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                  color: 'primary.contrastText',
                                  fontWeight: 600,
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255, 255, 255, 0.5)',
                                  },
                                  '& .MuiSvgIcon-root': {
                                    color: 'primary.contrastText',
                                  },
                                }}
                              >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="not active">Not Active</MenuItem>
                                <MenuItem value="locked">Locked</MenuItem>
                                <MenuItem value="finished">Finished</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                        </CardContent>

                        {/* Products in Room */}
                        <Box sx={{ p: 1 }}>
                          {roomProducts.map((product) => {
                            const productType = productTypes.find(
                              (pt) => pt.id === product.product_type_id
                            );
                            const isExpanded = expandedRows.has(product.id);
                            
                            return (
                              <Card 
                                key={product.id} 
                                sx={{ 
                                  mb: 1.5,
                                  border: selectedProducts.has(product.id) ? 2 : 1,
                                  borderColor: selectedProducts.has(product.id) ? 'primary.main' : 'divider',
                                }}
                              >
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {/* Product Header - name row only */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {!isFieldWorker && (
                                        <Checkbox
                                          checked={selectedProducts.has(product.id)}
                                          onChange={() => handleSelectProduct(product.id)}
                                          size="small"
                                          sx={{ p: 0.5 }}
                                        />
                                      )}
                                      <IconButton
                                        size="small"
                                        onClick={() => handleToggleRow(product.id)}
                                        sx={{ minWidth: 40, minHeight: 40 }}
                                      >
                                        {isExpanded ? (
                                          <ExpandLessIcon />
                                        ) : (
                                          <ExpandMoreIcon />
                                        )}
                                      </IconButton>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, overflowWrap: 'break-word' }}>
                                          {product.product_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {productType ? productType.productType : '-'}
                                        </Typography>
                                      </Box>
                                    </Box>

                                    {/* Product Info */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 6 }}>
                                      {product.note && (
                                        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'break-word', wordBreak: 'normal' }}>
                                          {product.note}
                                        </Typography>
                                      )}
                                      {/* Status + Actions below note */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                                        {product.status ? (
                                          <Chip 
                                            label={product.status} 
                                            size="small" 
                                            sx={{ width: 'fit-content' }}
                                          />
                                        ) : <Box />}
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                          <IconButton
                                            size="small"
                                            onClick={() => handlePrintBarcodes(product)}
                                            title="Print all barcodes"
                                            sx={{ minWidth: 40, minHeight: 40 }}
                                          >
                                            <PrintIcon fontSize="small" />
                                          </IconButton>
                                          {!isFieldWorker && (
                                            <IconButton
                                              size="small"
                                              onClick={() => handleOpenDialog(product)}
                                              sx={{ minWidth: 40, minHeight: 40 }}
                                            >
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                          )}
                                          {isAdmin && (
                                            <IconButton
                                              size="small"
                                              onClick={() => handleDelete(product.id)}
                                              sx={{ minWidth: 40, minHeight: 40 }}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          )}
                                        </Box>
                                      </Box>
                                    </Box>

                                    {/* Expanded Details */}
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ mt: 1, pl: 6 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            Product Details (Material: {productType ? productType.productType : '-'})
                                          </Typography>
                                          {!isFieldWorker && (
                                            <Button
                                              variant="outlined"
                                              size="small"
                                              startIcon={<AddIcon />}
                                              onClick={() => handleOpenDetailDialog(product.id)}
                                            >
                                              Add Detail
                                            </Button>
                                          )}
                                        </Box>
                                        {product.details && product.details.length > 0 ? (
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {product.details.map((detail) => {
                                              const totalCount = product.details?.length || 0;
                                              const detailName = generateDetailName(product, detail, totalCount);
                                              const isHardware = isHardwareDetail(detail);
                                              const showWidthHeight = (product.details || []).some((d) => !isHardwareDetail(d));
                                              
                                              return (
                                                <Card key={detail.id} variant="outlined" sx={{ p: 1.5 }}>
                                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                      {detailName}
                                                    </Typography>
                                                    {showWidthHeight && !isHardware && (
                                                      <Box sx={{ display: 'flex', gap: 2 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                          W: {detail.width || '-'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                          H: {detail.height || '-'}
                                                        </Typography>
                                                      </Box>
                                                    )}
                                                    <Autocomplete
                                                      freeSolo
                                                      size="small"
                                                      fullWidth
                                                      options={detailStatusOptions}
                                                      value={detail.status || ''}
                                                      inputValue={statusInputValue.get(detail.id) ?? (detail.status || '')}
                                                      disabled={isFieldWorker}
                                                      onInputChange={(_, newInputValue) => {
                                                        if (isFieldWorker) return;
                                                        const newMap = new Map(statusInputValue);
                                                        newMap.set(detail.id, newInputValue);
                                                        setStatusInputValue(newMap);
                                                      }}
                                                      onChange={async (_, newValue) => {
                                                        if (isFieldWorker) return;
                                                        const statusToSave = typeof newValue === 'string' ? newValue : null;
                                                        if (product && statusToSave) {
                                                          handleDetailStatusChange(product.id, detail.id, statusToSave);
                                                        }
                                                      }}
                                                      renderInput={(params) => (
                                                        <TextField
                                                          {...params}
                                                          label="Status"
                                                          variant="outlined"
                                                          size="small"
                                                        />
                                                      )}
                                                      getOptionLabel={(option) => {
                                                        return typeof option === 'string' ? option : '';
                                                      }}
                                                    />
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                      <Typography variant="caption" color="text.secondary">
                                                        Type: {detail.type_of_work || '-'}
                                                      </Typography>
                                                      {detail.barcode && (
                                                        <Typography variant="caption" color="text.secondary">
                                                          Barcode: {detail.barcode}
                                                        </Typography>
                                                      )}
                                                    </Box>
                                                    {(!isFieldWorker || isAdmin) && (
                                                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                        {!isFieldWorker && (
                                                          <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenDetailDialog(product.id, detail)}
                                                            sx={{ minWidth: 36, minHeight: 36 }}
                                                          >
                                                            <EditIcon fontSize="small" />
                                                          </IconButton>
                                                        )}
                                                        {isAdmin && (
                                                          <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteDetail(product.id, detail.id)}
                                                            sx={{ minWidth: 36, minHeight: 36 }}
                                                          >
                                                            <DeleteIcon fontSize="small" />
                                                          </IconButton>
                                                        )}
                                                      </Box>
                                                    )}
                                                  </Box>
                                                </Card>
                                              );
                                            })}
                                          </Box>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                            No product details
                                          </Typography>
                                        )}
                                      </Box>
                                    </Collapse>
                                  </Box>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </Box>
                      </Card>
                    );
                  });
                })()}
              </Box>
            ) : (
              // Desktop Table Layout
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        {!isFieldWorker && products.length > 0 && (
                          <Checkbox
                            indeterminate={selectedProducts.size > 0 && selectedProducts.size < products.length}
                            checked={products.length > 0 && selectedProducts.size === products.length}
                            onChange={handleSelectAll}
                          />
                        )}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell>Product Name</TableCell>
                      <TableCell>Product Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Note</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No fabrication orders found
                        </TableCell>
                      </TableRow>
                    ) : (() => {
                    let globalIndex = 0;
                    const paginatedRooms = Object.keys(paginatedProducts.byRoom).sort(naturalSort);

                    return paginatedRooms.flatMap((room) => {
                      const roomProducts = paginatedProducts.byRoom[room];
                      // Get room status - find the first product's job_id to look up room status
                      const firstProduct = roomProducts[0];
                      // Trim room name to match database (rooms are stored with trimmed names)
                      const trimmedRoom = room.trim();
                      const roomKey = firstProduct ? `${firstProduct.job_id}-${trimmedRoom}` : '';
                      const roomStatus = roomsMap.get(roomKey)?.status || 'active';
                      
                      return [
                        // Room header row
                        <TableRow key={`room-${room}`}>
                          <TableCell
                            colSpan={7}
                            sx={{
                              backgroundColor: 'primary.main',
                              color: 'primary.contrastText',
                              fontWeight: 600,
                              fontSize: '1rem',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <span>Room Name: "{room}"</span>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                  <Select
                                    value={roomStatus}
                                    onChange={(e) => {
                                      if (!firstProduct) return;
                                      const newStatus = e.target.value;
                                      
                                      // Find the room ID
                                      apiRequest(`/products/rooms/job/${firstProduct.job_id}`, { method: 'GET' })
                                        .then((roomsResponse) => {
                                          const roomsData = (roomsResponse.data || []) as Array<{ id: number; room_name: string; status: string }>;
                                          const roomData = roomsData.find(r => r.room_name === trimmedRoom);
                                          
                                          if (roomData) {
                                            return apiRequest(`/products/rooms/${roomData.id}`, {
                                              method: 'PUT',
                                              body: JSON.stringify({ status: newStatus }),
                                            });
                                          }
                                          throw new Error('Room not found');
                                        })
                                        .then(() => {
                                          // Update local state
                                          setRoomsMap((prev) => {
                                            const newMap = new Map(prev);
                                            newMap.set(roomKey, { status: newStatus });
                                            return newMap;
                                          });
                                          showSuccess('Room status updated successfully');
                                        })
                                        .catch((error: any) => {
                                          showError(error.message || 'Failed to update room status');
                                        });
                                    }}
                                    sx={{
                                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                      color: 'primary.contrastText',
                                      fontWeight: 600,
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                      },
                                      '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                      },
                                      '& .MuiSvgIcon-root': {
                                        color: 'primary.contrastText',
                                      },
                                    }}
                                  >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="not active">Not Active</MenuItem>
                                    <MenuItem value="locked">Locked</MenuItem>
                                    <MenuItem value="finished">Finished</MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>
                              {!isFieldWorker && firstProduct && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenCloneDialog(trimmedRoom, firstProduct.job_id)}
                                  sx={{
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    },
                                  }}
                                  title="Clone this room"
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>,
                        // Products in this room
                        ...roomProducts.map((product) => {
                          const productType = productTypes.find(
                            (pt) => pt.id === product.product_type_id
                          );
                          const currentIndex = globalIndex++;
                          return (
                            <Fragment key={product.id}>
                              <TableRow
                                hover
                                selected={selectedProducts.has(product.id)}
                                sx={{
                                  backgroundColor: currentIndex % 2 === 0 ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.12)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.20)',
                                  },
                                }}
                              >
                                <TableCell padding="checkbox">
                                  {!isFieldWorker && (
                                    <Checkbox
                                      checked={selectedProducts.has(product.id)}
                                      onChange={() => handleSelectProduct(product.id)}
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggleRow(product.id)}
                                  >
                                    {expandedRows.has(product.id) ? (
                                      <ExpandLessIcon />
                                    ) : (
                                      <ExpandMoreIcon />
                                    )}
                                  </IconButton>
                                </TableCell>
                                <TableCell>{product.product_name}</TableCell>
                                <TableCell>{productType ? productType.productType : '-'}</TableCell>
                                <TableCell>
                                  {product.status && (
                                    <Chip label={product.status} size="small" />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {product.note ? (
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                      {product.note}
                                    </Typography>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePrintBarcodes(product)}
                                    title="Print all barcodes"
                                  >
                                    <PrintIcon fontSize="small" />
                                  </IconButton>
                                  {!isFieldWorker && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenDialog(product)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                  {isAdmin && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDelete(product.id)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell
                                  style={{ paddingBottom: 0, paddingTop: 0 }}
                                  colSpan={7}
                                >
                                  <Collapse
                                    in={expandedRows.has(product.id)}
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    <Box sx={{ margin: 2 }}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 2,
                                        }}
                                      >
                                        <Typography variant="h6" gutterBottom>
                                          Product Details (Material : {productType ? productType.productType : '-'})
                                        </Typography>
                                        {!isFieldWorker && (
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => handleOpenDetailDialog(product.id)}
                                          >
                                            Add Detail
                                          </Button>
                                        )}
                                      </Box>
                                      {product.details && product.details.length > 0 ? (
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>Name</TableCell>
                                              {product.details.some((d) => !isHardwareDetail(d)) && (
                                                <>
                                                  <TableCell>Width</TableCell>
                                                  <TableCell>Height</TableCell>
                                                </>
                                              )}
                                          <TableCell
                                            onClick={() => !isFieldWorker && handleOpenBulkStatusDialog(product.id)}
                                            sx={{
                                              cursor: isFieldWorker ? 'default' : 'pointer',
                                              userSelect: 'none',
                                              '&:hover': {
                                                backgroundColor: isFieldWorker ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                                              },
                                            }}
                                          >
                                            Status
                                          </TableCell>
                                          <TableCell>Type of Work</TableCell>
                                          <TableCell>Barcode</TableCell>
                                          <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {product.details.map((detail, detailIndex) => {
                                              const totalCount = product.details?.length || 0;
                                              const detailName = generateDetailName(product, detail, totalCount);
                                              const isHardware = isHardwareDetail(detail);
                                              const showWidthHeight = (product.details || []).some((d) => !isHardwareDetail(d));
                                              return (
                                              <TableRow
                                                key={detail.id}
                                                sx={{
                                                  backgroundColor: detailIndex % 2 === 0 ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.12)',
                                                  '&:hover': {
                                                    backgroundColor: 'rgba(25, 118, 210, 0.20)',
                                                  },
                                                }}
                                              >
                                                <TableCell>
                                                  <Typography variant="body2" fontWeight="medium">
                                                    {detailName}
                                                  </Typography>
                                                </TableCell>
                                                {showWidthHeight && (
                                                  <>
                                                    {!isHardware ? (
                                                      <>
                                                        <TableCell>
                                                          {detail.width ? `${detail.width}` : '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                          {detail.height ? `${detail.height}` : '-'}
                                                        </TableCell>
                                                      </>
                                                    ) : (
                                                      <>
                                                        <TableCell>-</TableCell>
                                                        <TableCell>-</TableCell>
                                                      </>
                                                    )}
                                                  </>
                                                )}
                                                <TableCell>
                                                  <Autocomplete
                                                    freeSolo
                                                    size="small"
                                                    sx={{ minWidth: 150 }}
                                                    options={detailStatusOptions}
                                                    value={detail.status || ''}
                                                    inputValue={statusInputValue.get(detail.id) ?? (detail.status || '')}
                                                    disabled={isFieldWorker}
                                                    onInputChange={(_, newInputValue) => {
                                                      if (isFieldWorker) return;
                                                      const newMap = new Map(statusInputValue);
                                                      newMap.set(detail.id, newInputValue);
                                                      setStatusInputValue(newMap);
                                                    }}
                                                    onChange={async (_, newValue) => {
                                                      if (isFieldWorker) return;
                                                      let statusValue = typeof newValue === 'string' ? newValue : newValue || '';
                                                      
                                                      // Check if it's a "+ Create" option
                                                      if (statusValue.startsWith('__CREATE__:')) {
                                                        const actualStatusName = statusValue.replace('__CREATE__:', '');
                                                        handleOpenAddStatusModal(product.id, detail.id, actualStatusName);
                                                        return;
                                                      }
                                                      
                                                      // If empty, don't save - just update the input value
                                                      if (!statusValue || statusValue.trim() === '') {
                                                        statusValue = '';
                                                        const newMap = new Map(statusInputValue);
                                                        newMap.set(detail.id, statusValue);
                                                        setStatusInputValue(newMap);
                                                      } else {
                                                        // Check if status exists in the options
                                                        const exists = detailStatusOptions.includes(statusValue);
                                                        
                                                        if (exists) {
                                                          // Status exists, save immediately
                                                          await handleDetailStatusChange(product.id, detail.id, statusValue);
                                                          const newMap = new Map(statusInputValue);
                                                          newMap.set(detail.id, statusValue);
                                                          setStatusInputValue(newMap);
                                                        } else {
                                                          // Status doesn't exist, show modal to add it
                                                          handleOpenAddStatusModal(product.id, detail.id, statusValue);
                                                        }
                                                      }
                                                    }}
                                                    onBlur={async () => {
                                                      if (isFieldWorker) return;
                                                      // When user leaves the field, only save if a valid status is entered
                                                      const currentInput = statusInputValue.get(detail.id) ?? (detail.status || '');
                                                      let statusValue = currentInput.trim();
                                                      
                                                      // If empty, don't save - just clear the input
                                                      if (!statusValue || statusValue === '') {
                                                        const newMap = new Map(statusInputValue);
                                                        newMap.set(detail.id, '');
                                                        setStatusInputValue(newMap);
                                                      } else if (statusValue !== detail.status) {
                                                        // Only update if value changed
                                                        // Check if status exists
                                                        const exists = detailStatusOptions.includes(statusValue);
                                                        
                                                        if (exists) {
                                                          // Status exists, save it
                                                          await handleDetailStatusChange(product.id, detail.id, statusValue);
                                                          const newMap = new Map(statusInputValue);
                                                          newMap.set(detail.id, statusValue);
                                                          setStatusInputValue(newMap);
                                                        } else {
                                                          // Status doesn't exist, show modal to add it
                                                          handleOpenAddStatusModal(product.id, detail.id, statusValue);
                                                        }
                                                      }
                                                    }}
                                                    filterOptions={(options, params) => {
                                                      const filtered = options.filter((option) =>
                                                        option.toLowerCase().includes(params.inputValue.toLowerCase())
                                                      );
                                                      
                                                      // If input doesn't match any option and is not empty, add "+ Create" option
                                                      if (params.inputValue !== '' && params.inputValue.trim() !== '' && !filtered.includes(params.inputValue)) {
                                                        filtered.push(`__CREATE__:${params.inputValue}`);
                                                      }
                                                      
                                                      return filtered;
                                                    }}
                                                    getOptionLabel={(option) => {
                                                      if (typeof option === 'string' && option.startsWith('__CREATE__:')) {
                                                        return `+ Create "${option.replace('__CREATE__:', '')}"`;
                                                      }
                                                      return option || 'None';
                                                    }}
                                                    renderInput={(params) => (
                                                      <TextField
                                                        {...params}
                                                        size="small"
                                                        placeholder="Search or add status..."
                                                      />
                                                    )}
                                                    loading={createStatusSaving}
                                                  />
                                                </TableCell>
                                                <TableCell>
                                                  {detail.type_of_work ? (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                      {typeof detail.type_of_work === 'string' 
                                                        ? detail.type_of_work.split(',').map((work: string, idx: number) => {
                                                            const trimmedWork = work.trim();
                                                            return trimmedWork ? (
                                                              <Chip
                                                                key={idx}
                                                                label={trimmedWork}
                                                                size="small"
                                                                variant="outlined"
                                                              />
                                                            ) : null;
                                                          })
                                                        : Array.isArray(detail.type_of_work)
                                                        ? (detail.type_of_work as string[]).map((work: string, idx: number) => (
                                                            <Chip
                                                              key={idx}
                                                              label={work}
                                                              size="small"
                                                              variant="outlined"
                                                            />
                                                          ))
                                                        : null
                                                      }
                                                    </Box>
                                                  ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                      -
                                                    </Typography>
                                                  )}
                                                </TableCell>
                                                <TableCell>
                                                  {detail.barcode ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                      <Barcode
                                                        value={detail.barcode}
                                                        format="CODE128"
                                                        width={1}
                                                        height={40}
                                                        displayValue={false}
                                                        fontSize={12}
                                                      />
                                                    </Box>
                                                  ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                      -
                                                    </Typography>
                                                  )}
                                                </TableCell>
                                                <TableCell align="right">
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => handlePrintSingleBarcode(product, detail)}
                                                    title="Print barcode"
                                                    disabled={!detail.barcode}
                                                  >
                                                    <PrintIcon fontSize="small" />
                                                  </IconButton>
                                                  {!isFieldWorker && (
                                                    <IconButton
                                                      size="small"
                                                      onClick={() =>
                                                        handleOpenDetailDialog(product.id, detail)
                                                      }
                                                    >
                                                      <EditIcon fontSize="small" />
                                                    </IconButton>
                                                  )}
                                                  {isAdmin && (
                                                    <IconButton
                                                      size="small"
                                                      onClick={() =>
                                                        handleDeleteDetail(product.id, detail.id)
                                                      }
                                                    >
                                                      <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                              );
                                            })}
                                          </TableBody>
                                        </Table>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">
                                          No fabrication order details found
                                        </Typography>
                                      )}
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </Fragment>
                          );
                        }),
                      ];
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
            )}
            <TablePagination
              component="div"
              count={paginatedProducts.totalProducts}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  flexWrap: 'wrap',
                  gap: 1,
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                },
              }}
            />
          </>
        )}
      </Paper>

      {/* Product Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEdit ? 'Edit Fabrication Order' : 'Add Fabrication Order'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Product Name"
              value={form.product_name}
              onChange={(e) => setForm({ ...form, product_name: e.target.value })}
              required
              fullWidth
            />
            <Autocomplete
              freeSolo
              options={availableRooms}
              value={form.room || ''}
              onChange={(_, newValue) => {
                setForm({ ...form, room: typeof newValue === 'string' ? newValue : newValue || '' });
              }}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input') {
                  setForm({ ...form, room: newInputValue });
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Room" required fullWidth />
              )}
            />
            <Autocomplete
              freeSolo
              options={productTypes}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.productType || '';
              }}
              value={
                productTypes.find((pt) => pt.id.toString() === form.product_type_id) || null
              }
              inputValue={productTypeInputValue}
              onInputChange={(_, newInputValue, reason) => {
                setProductTypeInputValue(newInputValue);
                if (reason === 'clear') {
                  setForm({
                    ...form,
                    product_type_id: '',
                  });
                }
              }}
              onChange={(_, newValue) => {
                if (typeof newValue === 'string') {
                  // User typed a new value that doesn't exist
                  setCreateProductTypeForm({
                    ...createProductTypeForm,
                    productType: newValue,
                  });
                  setOpenCreateProductTypeDialog(true);
                } else if (newValue) {
                  // User selected an existing product type
                  setForm({
                    ...form,
                    product_type_id: newValue.id.toString(),
                  });
                  setProductTypeInputValue(newValue.productType || '');
                } else {
                  // User cleared the selection
                  setForm({
                    ...form,
                    product_type_id: '',
                  });
                  setProductTypeInputValue('');
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Product Type" fullWidth />
              )}
              isOptionEqualToValue={(option, value) => {
                if (typeof option === 'string' || typeof value === 'string') return false;
                return option.id === value.id;
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter((option) =>
                  option.productType.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                
                // If input doesn't match any existing option, allow creating new
                if (params.inputValue !== '' && !filtered.some(opt => opt.productType.toLowerCase() === params.inputValue.toLowerCase())) {
                  // Return the input value as a string option to allow creation
                  return [params.inputValue, ...filtered] as any;
                }
                
                return filtered;
              }}
            />
            <TextField
              label="Status"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              select
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Not Active">Not Active</MenuItem>
              <MenuItem value="Locked">Locked</MenuItem>
              <MenuItem value="Finished">Finished</MenuItem>
            </TextField>
            {!isEdit && (
              <TextField
                label="Number of Sections"
                type="number"
                value={form.number_of_sections}
                onChange={(e) => setForm({ ...form, number_of_sections: e.target.value })}
                required
                inputProps={{ min: 1 }}
                fullWidth
                helperText="This will create the specified number of product detail rows"
              />
            )}
            <TextField
              label="Note"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Detail Dialog */}
      <Dialog
        open={openDetailDialog}
        onClose={handleCloseDetailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditDetail ? 'Edit Fabrication Order Detail' : 'Add Fabrication Order Detail'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {!isHardwareProduct() && (
              <>
                <TextField
                  label="Width"
                  type="number"
                  value={detailForm.width}
                  onChange={(e) => setDetailForm({ ...detailForm, width: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Height"
                  type="number"
                  value={detailForm.height}
                  onChange={(e) => setDetailForm({ ...detailForm, height: e.target.value })}
                  fullWidth
                />
              </>
            )}
            <Autocomplete
              freeSolo
              options={detailStatusOptions}
              value={detailForm.status || ''}
              inputValue={statusInputValue.get(-1) ?? (detailForm.status || '')}
              onInputChange={(_, newInputValue) => {
                const newMap = new Map(statusInputValue);
                newMap.set(-1, newInputValue);
                setStatusInputValue(newMap);
              }}
              onChange={async (_, newValue) => {
                let statusValue = typeof newValue === 'string' ? newValue : newValue || '';
                
                // Check if it's a "+ Create" option
                if (statusValue.startsWith('__CREATE__:')) {
                  const actualStatusName = statusValue.replace('__CREATE__:', '');
                  handleOpenAddStatusModal(-1, -1, actualStatusName);
                  return;
                }
                
                // If empty, set to empty string (will be saved as 'None' when form is submitted)
                if (!statusValue || statusValue.trim() === '') {
                  statusValue = '';
                  setDetailForm({ ...detailForm, status: statusValue });
                  const newMap = new Map(statusInputValue);
                  newMap.set(-1, statusValue);
                  setStatusInputValue(newMap);
                } else {
                  // Check if status exists in the options
                  const exists = detailStatusOptions.includes(statusValue);
                  
                  if (exists) {
                    // Status exists, just update the form
                    setDetailForm({ ...detailForm, status: statusValue });
                    const newMap = new Map(statusInputValue);
                    newMap.set(-1, statusValue);
                    setStatusInputValue(newMap);
                  } else {
                    // Status doesn't exist, show modal to add it
                    // We'll need to store this for when the dialog is saved
                    setDetailForm({ ...detailForm, status: statusValue });
                    handleOpenAddStatusModal(-1, -1, statusValue); // Use -1 as placeholder for dialog form
                  }
                }
              }}
              onBlur={async () => {
                // When user leaves the field, save the current input value
                const currentInput = statusInputValue.get(-1) ?? (detailForm.status || '');
                let statusValue = currentInput.trim();
                
                // If empty, keep it empty (will be saved as 'None' when form is submitted)
                if (!statusValue || statusValue === '') {
                  setDetailForm({ ...detailForm, status: '' });
                } else if (statusValue !== detailForm.status) {
                  // Only update if value changed
                  // Check if status exists
                  const exists = detailStatusOptions.includes(statusValue);
                  
                  if (exists) {
                    // Status exists, just update the form
                    setDetailForm({ ...detailForm, status: statusValue });
                    const newMap = new Map(statusInputValue);
                    newMap.set(-1, statusValue);
                    setStatusInputValue(newMap);
                  } else {
                    // Status doesn't exist, show modal to add it
                    handleOpenAddStatusModal(-1, -1, statusValue);
                  }
                }
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter((option) =>
                  option.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                
                // If input doesn't match any option and is not empty, add "+ Create" option
                if (params.inputValue !== '' && params.inputValue.trim() !== '' && !filtered.includes(params.inputValue)) {
                  filtered.push(`__CREATE__:${params.inputValue}`);
                }
                
                return filtered;
              }}
              getOptionLabel={(option) => {
                if (typeof option === 'string' && option.startsWith('__CREATE__:')) {
                  return `+ Create "${option.replace('__CREATE__:', '')}"`;
                }
                return option || 'None';
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Status"
                  placeholder="Search or add status..."
                  fullWidth
                />
              )}
              loading={createStatusSaving}
            />
            <Autocomplete
              multiple
              freeSolo
              options={typeOfWorkOptions}
              value={detailForm.typeOfWork || []}
              inputValue={typeOfWorkInputValue.get(-1) ?? ''}
              onInputChange={(_, newInputValue) => {
                const newMap = new Map(typeOfWorkInputValue);
                newMap.set(-1, newInputValue);
                setTypeOfWorkInputValue(newMap);
              }}
              onChange={async (_, newValue) => {
                const values = Array.isArray(newValue) ? newValue : [];
                const processedValues: string[] = [];
                
                for (const val of values) {
                  if (typeof val === 'string') {
                    // Check if it's a "+ Create" option
                    if (val.startsWith('__CREATE__:')) {
                      const actualWorkName = val.replace('__CREATE__:', '').trim();
                      if (actualWorkName) {
                        const createdName = await handleCreateTypeOfWork(actualWorkName, false);
                        if (createdName) {
                          processedValues.push(createdName);
                        }
                      }
                    } else if (val.trim()) {
                      processedValues.push(val.trim());
                    }
                  }
                }
                
                setDetailForm({ ...detailForm, typeOfWork: processedValues });
                const newMap = new Map(typeOfWorkInputValue);
                newMap.set(-1, '');
                setTypeOfWorkInputValue(newMap);
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter((option) =>
                  option.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                
                // If input doesn't match any option and is not empty, add "+ Create" option
                if (params.inputValue !== '' && params.inputValue.trim() !== '' && !filtered.includes(params.inputValue)) {
                  filtered.push(`__CREATE__:${params.inputValue}`);
                }
                
                return filtered;
              }}
              getOptionLabel={(option) => {
                if (typeof option === 'string' && option.startsWith('__CREATE__:')) {
                  return `+ Create "${option.replace('__CREATE__:', '')}"`;
                }
                return option || '';
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Type of Work"
                  placeholder="Search or add type of work..."
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                        {detailForm.typeOfWork && detailForm.typeOfWork.length > 0 && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCopiedTypeOfWork([...detailForm.typeOfWork]);
                                showSuccess(`Copied ${detailForm.typeOfWork.length} type(s) of work`);
                              }}
                              title="Copy type of work"
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )}
                        {copiedTypeOfWork.length > 0 && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Merge copied values with existing ones, avoiding duplicates
                                const existing = detailForm.typeOfWork || [];
                                const merged = [...existing];
                                copiedTypeOfWork.forEach((val) => {
                                  if (!merged.includes(val)) {
                                    merged.push(val);
                                  }
                                });
                                setDetailForm({ ...detailForm, typeOfWork: merged });
                                showSuccess(`Pasted ${copiedTypeOfWork.length} type(s) of work`);
                              }}
                              title={`Paste ${copiedTypeOfWork.length} copied type(s) of work`}
                            >
                              <ContentPasteIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    key={option}
                  />
                ))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailDialog}>Cancel</Button>
          <Button onClick={handleSaveDetail} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Product Type Dialog */}
      <Dialog
        open={openCreateProductTypeDialog}
        onClose={() => {
          setOpenCreateProductTypeDialog(false);
          setCreateProductTypeForm({
            productType: '',
            description: '',
            sellingPrice: '',
            unit: 'Sq ft',
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Product Type</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Product Type"
              value={createProductTypeForm.productType}
              onChange={(e) =>
                setCreateProductTypeForm({
                  ...createProductTypeForm,
                  productType: e.target.value,
                })
              }
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={createProductTypeForm.description}
              onChange={(e) =>
                setCreateProductTypeForm({
                  ...createProductTypeForm,
                  description: e.target.value,
                })
              }
              multiline
              rows={2}
              fullWidth
            />
            <TextField
              label="Selling Price"
              type="number"
              value={createProductTypeForm.sellingPrice}
              onChange={(e) =>
                setCreateProductTypeForm({
                  ...createProductTypeForm,
                  sellingPrice: e.target.value,
                })
              }
              required
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label="Unit"
              value={createProductTypeForm.unit}
              onChange={(e) =>
                setCreateProductTypeForm({
                  ...createProductTypeForm,
                  unit: e.target.value,
                })
              }
              select
              fullWidth
            >
              <MenuItem value="Sq ft">Sq ft</MenuItem>
              <MenuItem value="Ln ft">Ln ft</MenuItem>
              <MenuItem value="Hardware">Hardware</MenuItem>
              <MenuItem value="item">item</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenCreateProductTypeDialog(false);
              setCreateProductTypeForm({
                productType: '',
                description: '',
                sellingPrice: '',
                unit: 'Sq ft',
              });
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateProductType}
            variant="contained"
            disabled={
              createProductTypeSaving ||
              !createProductTypeForm.productType ||
              !createProductTypeForm.sellingPrice
            }
          >
            {createProductTypeSaving ? 'Saving...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Status Change Dialog */}
      <Dialog
        open={openBulkStatusDialog}
        onClose={handleCloseBulkStatusDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change All Statuses</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="warning">
              You are about to change the status for all product details in this fabrication order. 
              This action will update {bulkStatusProductId ? (allProducts.find((p) => p.id === bulkStatusProductId)?.details?.length || 0) : 0} product detail(s).
            </Alert>
            <Autocomplete
              freeSolo
              options={detailStatusOptions}
              value={bulkStatusValue}
              disabled={isFieldWorker}
              onChange={(_, newValue) => {
                if (isFieldWorker) return;
                setBulkStatusValue(typeof newValue === 'string' ? newValue : newValue || '');
              }}
              onInputChange={(_, newInputValue) => {
                if (isFieldWorker) return;
                setBulkStatusValue(newInputValue);
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter((option) =>
                  option.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                
                // If input doesn't match any option and is not empty, add "+ Create" option
                if (params.inputValue !== '' && params.inputValue.trim() !== '' && !filtered.includes(params.inputValue)) {
                  filtered.push(`__CREATE__:${params.inputValue}`);
                }
                
                return filtered;
              }}
              getOptionLabel={(option) => {
                if (typeof option === 'string' && option.startsWith('__CREATE__:')) {
                  return `+ Create "${option.replace('__CREATE__:', '')}"`;
                }
                return option || 'None';
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Status"
                  placeholder="Select or type a status..."
                  fullWidth
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkStatusDialog} disabled={bulkStatusSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkStatusChange}
            variant="contained"
            disabled={bulkStatusSaving || isFieldWorker}
            color="warning"
          >
            {bulkStatusSaving ? 'Updating...' : 'Update All Statuses'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Product Detail Status Dialog */}
      <Dialog
        open={openAddStatusDialog}
        onClose={() => {
          setOpenAddStatusDialog(false);
          setPendingStatus(null);
          setNewStatusForm({ statusName: '', description: '', color: '' });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Product Detail Status</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Status Name"
              value={newStatusForm.statusName}
              onChange={(e) => setNewStatusForm({ ...newStatusForm, statusName: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={newStatusForm.description}
              onChange={(e) => setNewStatusForm({ ...newStatusForm, description: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Color
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Box
                  component="input"
                  type="color"
                  value={newStatusForm.color || '#FF5733'}
                  onChange={(e) => {
                    const colorValue = e.target.value;
                    setNewStatusForm({ ...newStatusForm, color: colorValue });
                  }}
                  sx={{
                    width: 60,
                    height: 40,
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    cursor: 'pointer',
                    padding: 0,
                    '&::-webkit-color-swatch-wrapper': {
                      padding: 0,
                    },
                    '&::-webkit-color-swatch': {
                      border: 'none',
                      borderRadius: 1,
                    },
                  }}
                />
                <TextField
                  label="Hex code"
                  value={newStatusForm.color}
                  onChange={(e) => {
                    let colorValue = e.target.value;
                    // Ensure it starts with # if it doesn't
                    if (colorValue && !colorValue.startsWith('#')) {
                      colorValue = '#' + colorValue;
                    }
                    setNewStatusForm({ ...newStatusForm, color: colorValue });
                  }}
                  placeholder="#FF5733"
                  sx={{ flex: 1 }}
                  inputProps={{
                    pattern: '^#[0-9A-Fa-f]{6}$',
                    maxLength: 7,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenAddStatusDialog(false);
              setPendingStatus(null);
              setNewStatusForm({ statusName: '', description: '', color: '' });
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewStatus}
            variant="contained"
            disabled={createStatusSaving || !newStatusForm.statusName}
          >
            {createStatusSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone Room Dialog */}
      <Dialog
        open={openCloneDialog}
        onClose={handleCloseCloneDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Clone Room</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info">
              This will clone room "{cloneSourceRoom}" with all its products and product details to multiple new rooms.
            </Alert>
            <TextField
              label="Source Room"
              value={cloneSourceRoom}
              disabled
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Start Room Number"
                type="number"
                value={cloneRoomRange.start}
                onChange={(e) => setCloneRoomRange({ ...cloneRoomRange, start: e.target.value })}
                fullWidth
                required
                inputProps={{ min: 1 }}
                helperText="First room number (e.g., 1)"
              />
              <TextField
                label="End Room Number"
                type="number"
                value={cloneRoomRange.end}
                onChange={(e) => setCloneRoomRange({ ...cloneRoomRange, end: e.target.value })}
                fullWidth
                required
                inputProps={{ min: 1 }}
                helperText="Last room number (e.g., 150)"
              />
            </Box>
            {cloneRoomRange.start && cloneRoomRange.end && 
             !isNaN(parseInt(cloneRoomRange.start)) && 
             !isNaN(parseInt(cloneRoomRange.end)) && (() => {
              const start = parseInt(cloneRoomRange.start);
              const end = parseInt(cloneRoomRange.end);
              const count = end - start + 1;
              
              // Extract prefix from source room name
              const sourceRoomTrimmed = cloneSourceRoom.trim();
              const numberMatch = sourceRoomTrimmed.match(/(\d+)$/);
              let roomPrefix = sourceRoomTrimmed;
              if (numberMatch) {
                const numberIndex = sourceRoomTrimmed.lastIndexOf(numberMatch[1]);
                roomPrefix = sourceRoomTrimmed.substring(0, numberIndex).trim();
              }
              
              // Generate preview room names
              const firstRoomName = roomPrefix ? `${roomPrefix} ${start}` : start.toString();
              const lastRoomName = roomPrefix ? `${roomPrefix} ${end}` : end.toString();
              const previewText = count <= 3 
                ? `Room names: ${Array.from({ length: count }, (_, i) => roomPrefix ? `${roomPrefix} ${start + i}` : (start + i).toString()).join(', ')}`
                : `Room names: "${firstRoomName}", "${firstRoomName.split(' ')[0]} ${start + 1}", ..., "${lastRoomName}"`;
              
              return (
                <Alert severity={count > 200 ? 'warning' : 'success'}>
                  This will create {count} room(s). {previewText}
                </Alert>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCloneDialog} disabled={cloneSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleCloneRoom}
            variant="contained"
            disabled={cloneSaving || !cloneRoomRange.start || !cloneRoomRange.end}
            color="primary"
          >
            {cloneSaving ? 'Cloning...' : 'Clone Room'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
